var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "nativewind/jsx-runtime";
import Clipboard from '@react-native-clipboard/clipboard';
import dayjs from 'dayjs';
import React, { useMemo } from 'react';
import { Image, Linking, Platform } from 'react-native';
import styled from 'styled-components/native';
import SccRemoteImage from '@/components/SccRemoteImage';
import RoadView from '@/components/maps/RoadView';
import StoreAddressIcon from '@/assets/icon/ic_store_address_fill.svg';
import StoreInfoIcon from '@/assets/icon/ic_store_info_fill.svg';
import KakaoReviewIcon from '@/assets/icon/ic_review_kakao.svg';
import RouteFillIcon from '@/assets/icon/ic_route_fill.svg';
import { isInfoRequestEligible } from '@/components/AccessibilityInfoRequestButton';
import { SccTouchableOpacity } from '@/components/SccTouchableOpacity';
import { color } from '@/constant/color';
import { font } from '@/constant/font';
import useNavigation from '@/navigation/useNavigation';
import ToastUtils from '@/utils/ToastUtils';
import PlaceDetailPlaceReviewItem from '../../PlaceDetailScreen/components/PlaceReviewItem';
import PlaceReviewSummaryInfo from '../../PlaceDetailScreen/components/PlaceReviewSummaryInfo';
import PlaceDetailPlaceToiletReviewItem from '../../PlaceDetailScreen/components/PlaceToiletReviewItem';
import { EmptyStateCard, FloorInfoRow, } from '../components/AccessibilityInfoComponents';
import { BuildingEntranceSection, BuildingEntranceEmptySection, PlaceEntranceSection, FloorMovementSection, } from '../components/EntranceSection';
import { getAccessibilitySections } from '../components/PlaceInfo.utils';
export default function V2HomeTab({ accessibility, place, building: _building, reviews, toiletReviews, kakaoPlaceId, isAccessibilityInfoRequested, isAccessibilityRegistrable: _isAccessibilityRegistrable, onRequestInfo, onPressAccessibilityTab: _onPressAccessibilityTab, onPressReviewTab: _onPressReviewTab, onPressDirections, onPressPlaceRegister, onPressBuildingRegister, onPressReviewRegister: _onPressReviewRegister, onPressToiletRegister, }) {
    var _a, _b, _c, _d, _e, _f;
    const navigation = useNavigation();
    // 다중 출입구 배열 지원 (하위 호환: 단일 필드 fallback)
    const placeAccessibilities = (_a = accessibility === null || accessibility === void 0 ? void 0 : accessibility.placeAccessibilities) !== null && _a !== void 0 ? _a : ((accessibility === null || accessibility === void 0 ? void 0 : accessibility.placeAccessibility)
        ? [accessibility.placeAccessibility]
        : []);
    const buildingAccessibilities = (_b = accessibility === null || accessibility === void 0 ? void 0 : accessibility.buildingAccessibilities) !== null && _b !== void 0 ? _b : ((accessibility === null || accessibility === void 0 ? void 0 : accessibility.buildingAccessibility)
        ? [accessibility.buildingAccessibility]
        : []);
    const hasAccessibility = placeAccessibilities.length > 0;
    const hasBuildingAccessibility = buildingAccessibilities.length > 0;
    // V2 건물유형 분기 필드 (첫 번째 PA 기준)
    const placeAcc = placeAccessibilities[0];
    const isStandalone = (placeAcc === null || placeAcc === void 0 ? void 0 : placeAcc.isStandaloneBuilding) === true;
    const doorDir = placeAcc === null || placeAcc === void 0 ? void 0 : placeAcc.doorDirectionType;
    const floors = (_c = placeAcc === null || placeAcc === void 0 ? void 0 : placeAcc.floors) !== null && _c !== void 0 ? _c : [];
    const isMultiFloor = floors.length > 1;
    const hasV2Fields = (placeAcc === null || placeAcc === void 0 ? void 0 : placeAcc.isStandaloneBuilding) != null && doorDir != null;
    // 섹션 순서/가시성을 공유 유틸리티로 결정 (홈탭에서는 '내부 이용 정보' 제외)
    const sections = getAccessibilitySections({
        isStandalone,
        doorDir,
        isMultiFloor,
        hasV2Fields,
        hasBuildingAccessibility,
    });
    // 접근성 데이터에서 첫 번째 challengeCrusherGroup 추출
    const challengeCrusherGroup = (_e = (_d = placeAccessibilities.find(pa => pa.challengeCrusherGroup)) === null || _d === void 0 ? void 0 : _d.challengeCrusherGroup) !== null && _e !== void 0 ? _e : (_f = buildingAccessibilities.find(ba => ba.challengeCrusherGroup)) === null || _f === void 0 ? void 0 : _f.challengeCrusherGroup;
    // ── 가게정보 helpers ──
    const onCopy = () => {
        Clipboard.setString(place.address);
        ToastUtils.show('주소가 복사되었습니다.');
    };
    const getExternalUrl = (hash) => {
        if (kakaoPlaceId) {
            return `https://place.map.kakao.com/${kakaoPlaceId}#${hash}`;
        }
        const searchQuery = encodeURIComponent(`${place.name} ${place.address}`);
        return `https://map.naver.com/p/search/${searchQuery}`;
    };
    const onOpenBusinessHours = () => __awaiter(this, void 0, void 0, function* () {
        const url = getExternalUrl('home');
        if (Platform.OS === 'web') {
            yield Linking.openURL(url);
            return;
        }
        navigation.navigate('Webview', { url, headerVariant: 'navigation' });
    });
    const onOpenKakaoReview = () => __awaiter(this, void 0, void 0, function* () {
        const url = getExternalUrl('review');
        if (Platform.OS === 'web') {
            yield Linking.openURL(url);
            return;
        }
        navigation.navigate('Webview', { url, headerVariant: 'navigation' });
    });
    // 대표 리뷰 선정: 사진 있는 것 우선 → 최신순
    const representativeReview = useMemo(() => {
        if (reviews.length === 0)
            return undefined;
        return [...reviews].sort((a, b) => {
            var _a, _b, _c, _d, _e, _f, _g, _h;
            const aHasImages = ((_b = (_a = a.images) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0) > 0 ? 1 : 0;
            const bHasImages = ((_d = (_c = b.images) === null || _c === void 0 ? void 0 : _c.length) !== null && _d !== void 0 ? _d : 0) > 0 ? 1 : 0;
            if (bHasImages !== aHasImages)
                return bHasImages - aHasImages;
            return ((_f = (_e = b.createdAt) === null || _e === void 0 ? void 0 : _e.value) !== null && _f !== void 0 ? _f : 0) - ((_h = (_g = a.createdAt) === null || _g === void 0 ? void 0 : _g.value) !== null && _h !== void 0 ? _h : 0);
        })[0];
    }, [reviews]);
    // ── 접근성 날짜는 각 출입구별로 계산 (렌더링 시 인라인) ──
    return (_jsxs(Container, { children: [_jsxs(PlaceInfoSection, { children: [_jsx(PlaceInfoTitle, { children: "\uAC00\uAC8C\uC815\uBCF4" }), _jsxs(PlaceInfoContent, { children: [_jsxs(PlaceInfoRow, { children: [_jsx(StoreAddressIcon, { width: 20, height: 20, color: color.gray30 }), _jsxs(AddressCopyWrapper, { children: [_jsx(AddressText, { children: place.address }), _jsx(CopyButton, { elementName: "v2_home_tab_copy_address", onPress: onCopy, children: _jsx(CopyText, { children: "\uBCF5\uC0AC" }) })] })] }), _jsxs(PlaceInfoRow, { children: [_jsx(StoreInfoIcon, { width: 20, height: 20, color: color.gray30 }), _jsx(ExternalLinkButton, { elementName: "v2_home_tab_business_hours", onPress: onOpenBusinessHours, children: _jsx(ExternalLinkText, { children: "\uC601\uC5C5\uC2DC\uAC04 \uBC0F \uBA54\uB274" }) })] }), _jsxs(PlaceInfoRow, { children: [_jsx(KakaoReviewIcon, { width: 20, height: 20, color: color.gray30 }), _jsx(ExternalLinkButton, { elementName: "v2_home_tab_kakao_review", onPress: onOpenKakaoReview, children: _jsx(ExternalLinkText, { children: "\uCE74\uCE74\uC624 \uB9AC\uBDF0" }) })] }), _jsxs(PlaceInfoRow, { children: [_jsx(RouteFillIcon, { width: 20, height: 20, color: color.gray30 }), _jsx(ExternalLinkButton, { elementName: "v2_home_tab_directions", onPress: onPressDirections, children: _jsx(ExternalLinkText, { children: "\uAE38\uCC3E\uAE30" }) })] })] })] }), !hasAccessibility && place.location && (_jsx(RoadView, { position: { lat: place.location.lat, lng: place.location.lng }, name: place.name })), isInfoRequestEligible({
                hasPlaceAccessibility: hasAccessibility,
                address: place.address,
                category: place.category,
            }) && (_jsx(AskBannerContainer, { children: _jsxs(AskBannerInner, { children: [_jsx(AskBannerText, { children: '이곳의 접근성이 궁금하시면\n버튼을 눌러서 접근성 정보를 요청해주세요!' }), _jsx(RequestButton, { elementName: "v2_home_tab_request_info", onPress: onRequestInfo, isRequested: isAccessibilityInfoRequested, children: _jsx(RequestButtonText, { isRequested: isAccessibilityInfoRequested, children: isAccessibilityInfoRequested
                                    ? '정보 요청 완료!'
                                    : '정보 요청하기' }) }), _jsx(AskBannerImage, { source: require('@/assets/img/img_request_info_banner.png') })] }) })), _jsxs(Section, { children: [_jsxs(SectionHeader, { children: [_jsx(SectionTitle, { children: "\uC811\uADFC\uC131" }), hasAccessibility && (_jsx(ConquerorText, { children: formatConquerorText(accessibility) }))] }), hasAccessibility ? (_jsx(AccessibilitySectionContainer, { children: sections.map(section => {
                            switch (section) {
                                case '층 정보':
                                    return (_jsx(FloorInfoRow, { accessibility: accessibility }, section));
                                case '건물 출입구':
                                    return (_jsx(React.Fragment, { children: hasBuildingAccessibility ? (buildingAccessibilities.map((ba, index) => {
                                            var _a, _b, _c;
                                            return (_jsx(BuildingEntranceSection, { buildingDate: dayjs((_b = (_a = ba.createdAt) === null || _a === void 0 ? void 0 : _a.value) !== null && _b !== void 0 ? _b : Date.now()).format('YYYY.MM.DD'), buildingAccessibility: ba, title: buildingAccessibilities.length > 1
                                                    ? `건물 출입구 (${index + 1})`
                                                    : '건물 출입구', compact: true }, (_c = ba.id) !== null && _c !== void 0 ? _c : index));
                                        })) : (_jsx(BuildingEntranceEmptySection, { compact: true, onRegister: onPressBuildingRegister })) }, section));
                                case '매장 출입구':
                                    return placeAccessibilities.length > 0 ? (_jsx(PlaceEntranceSection, { title: '매장 출입구', placeDate: dayjs(placeAccessibilities[0].createdAt.value).format('YYYY.MM.DD'), placeAccessibility: placeAccessibilities[0], compact: true }, section)) : null;
                                case '층간 이동 정보':
                                    return (_jsx(FloorMovementSection, { compact: true, placeAccessibility: placeAcc }, section));
                                case '내부 이용 정보':
                                    return null; // 홈탭에서는 제외
                                default:
                                    return null;
                            }
                        }) })) : (_jsx(EmptyStateCard, { title: '등록된 접근성 정보가 없어요🥲', description: '아래 버튼을 눌러서 정보를 등록해주세요.', buttonText: "\uC815\uBCF4 \uB4F1\uB85D\uD558\uAE30", onPress: onPressPlaceRegister, elementName: "v2_home_tab_register_place" }))] }), challengeCrusherGroup ? (_jsxs(B2BLabelBanner, { children: [challengeCrusherGroup.icon && (_jsx(SccRemoteImage, { imageUrl: challengeCrusherGroup.icon.imageUrl, resizeMode: "contain", wrapperBackgroundColor: null, fixedHeight: 22.4 })), _jsx(B2BBannerText, { children: "\uC774 \uACC4\uB2E8\uBFCC\uC154\uD074\uB7FD\uACFC \uD568\uAED8\uD588\uC5B4\uC694." })] })) : (_jsx(ThickDivider, {})), reviews.length > 0 && (_jsxs(_Fragment, { children: [_jsxs(Section, { children: [_jsx(SectionHeader, { children: _jsx(SectionTitle, { children: "\uBC29\uBB38 \uB9AC\uBDF0" }) }), _jsxs(ReviewContent, { children: [_jsx(PlaceReviewSummaryInfo, { reviews: reviews, placeId: place.id, placeName: place.name, placeLocation: place.location, placeAddress: place.address, hideWriteButton: true }), representativeReview && (_jsxs(_Fragment, { children: [_jsx(ReviewDivider, {}), _jsx(PlaceDetailPlaceReviewItem, { placeId: place.id, review: representativeReview })] }))] })] }), _jsx(ThickDivider, {})] })), _jsxs(Section, { children: [_jsx(SectionHeader, { children: _jsx(SectionTitle, { children: "\uD654\uC7A5\uC2E4" }) }), toiletReviews.length > 0 ? (_jsx(ToiletReviewList, { children: toiletReviews.map((review, idx) => (_jsxs(React.Fragment, { children: [_jsx(PlaceDetailPlaceToiletReviewItem, { placeId: place.id, review: review }), idx !== toiletReviews.length - 1 && _jsx(ToiletDivider, {})] }, review.id))) })) : (_jsx(EmptyStateCard, { title: '등록된 장애인 화장실 정보가 없어요🥲', description: '장애인 화장실을 등록해주시면 큰 도움이 됩니다.', buttonText: "\uC7A5\uC560\uC778 \uD654\uC7A5\uC2E4 \uC815\uBCF4 \uB4F1\uB85D", onPress: onPressToiletRegister, elementName: "v2_home_tab_register_toilet" }))] })] }));
}
// ──────────────── Helpers ────────────────
function formatConquerorText(accessibility) {
    var _a, _b;
    const nameSet = new Set();
    const placeItems = ((_a = accessibility === null || accessibility === void 0 ? void 0 : accessibility.placeAccessibilities) === null || _a === void 0 ? void 0 : _a.length)
        ? accessibility.placeAccessibilities
        : (accessibility === null || accessibility === void 0 ? void 0 : accessibility.placeAccessibility)
            ? [accessibility.placeAccessibility]
            : [];
    for (const pa of placeItems) {
        if (pa.registeredUserName) {
            nameSet.add(pa.registeredUserName);
        }
    }
    const buildingItems = ((_b = accessibility === null || accessibility === void 0 ? void 0 : accessibility.buildingAccessibilities) === null || _b === void 0 ? void 0 : _b.length)
        ? accessibility.buildingAccessibilities
        : (accessibility === null || accessibility === void 0 ? void 0 : accessibility.buildingAccessibility)
            ? [accessibility.buildingAccessibility]
            : [];
    for (const ba of buildingItems) {
        if (ba.registeredUserName) {
            nameSet.add(ba.registeredUserName);
        }
    }
    const names = Array.from(nameSet);
    if (names.length === 0) {
        return '정복자 익명 비밀요원';
    }
    if (names.length === 1) {
        return `정복자 ${names[0]}`;
    }
    return `정복자 ${names[0]} 외 ${names.length - 1}명`;
}
// ──────────────── Styled Components ────────────────
const Container = styled.View `
  background-color: ${color.white};
`;
/* 1. 가게정보 섹션 */
const PlaceInfoSection = styled.View `
  padding: 20px;
  gap: 16px;
`;
const PlaceInfoTitle = styled.Text `
  font-family: ${font.pretendardSemibold};
  font-size: 16px;
  line-height: 24px;
  letter-spacing: -0.32px;
  color: ${color.black};
`;
const PlaceInfoContent = styled.View `
  gap: 10px;
`;
const PlaceInfoRow = styled.View `
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;
const AddressCopyWrapper = styled.View `
  flex-direction: row;
  align-items: center;
  gap: 4px;
  flex-shrink: 1;
`;
const AddressText = styled.Text `
  font-family: ${font.pretendardRegular};
  font-size: 14px;
  line-height: 20px;
  letter-spacing: -0.28px;
  color: ${color.gray70};
  flex-shrink: 1;
`;
const CopyButton = styled(SccTouchableOpacity) `
  flex-direction: row;
  align-items: center;
  gap: 4px;
`;
const CopyText = styled.Text `
  font-family: ${font.pretendardRegular};
  font-size: 12px;
  line-height: 16px;
  letter-spacing: -0.24px;
  color: ${color.brand50};
`;
const ExternalLinkButton = styled(SccTouchableOpacity) ``;
const ExternalLinkText = styled.Text `
  font-family: ${font.pretendardRegular};
  font-size: 14px;
  line-height: 20px;
  letter-spacing: -0.28px;
  color: ${color.gray70};
  text-decoration-line: underline;
`;
/* 2. AskBanner */
const AskBannerContainer = styled.View `
  padding-vertical: 20px;
`;
const AskBannerInner = styled.View `
  background-color: ${color.gray15};
  padding: 20px;
  overflow: hidden;
  gap: 12px;
`;
const AskBannerText = styled.Text `
  font-family: ${font.pretendardMedium};
  font-size: 14px;
  line-height: 22px;
  letter-spacing: -0.28px;
  color: ${color.black};
`;
const RequestButton = styled(SccTouchableOpacity) `
  background-color: ${({ isRequested }) => isRequested ? color.gray25 : color.brand40};
  border-radius: 100px;
  padding-horizontal: 16px;
  padding-vertical: 6px;
  align-self: flex-start;
`;
const RequestButtonText = styled.Text `
  font-family: ${font.pretendardMedium};
  font-size: 12px;
  line-height: 16px;
  letter-spacing: -0.24px;
  color: ${({ isRequested }) => (isRequested ? '#3A3C45' : color.white)};
`;
const AskBannerImage = styled(Image) `
  position: absolute;
  right: 20px;
  top: 4px;
  width: 90px;
  height: 116px;
  resize-mode: contain;
`;
/* Section common */
const Section = styled.View `
  padding: 20px;
  gap: 16px;
`;
const SectionHeader = styled.View `
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;
const AccessibilitySectionContainer = styled.View `
  flex-direction: column;
  gap: 40px;
`;
const SectionTitle = styled.Text `
  font-family: ${font.pretendardSemibold};
  font-size: 18px;
  line-height: 26px;
  letter-spacing: -0.36px;
  color: ${color.gray90};
`;
const ConquerorText = styled.Text `
  flex: 1;
  font-family: ${font.pretendardRegular};
  font-size: 12px;
  line-height: 16px;
  letter-spacing: -0.24px;
  color: #767884;
  text-align: right;
`;
/* Review content — matches V2ReviewTab Content gap */
const ReviewContent = styled.View `
  gap: 32px;
`;
const ReviewDivider = styled.View `
  height: 1px;
  background-color: ${color.gray20};
`;
/* Toilet reviews */
const ToiletReviewList = styled.View `
  gap: 20px;
`;
const ToiletDivider = styled.View `
  height: 1px;
  background-color: ${color.gray20};
`;
/* Thick Divider */
const ThickDivider = styled.View `
  height: 6px;
  background-color: ${color.gray5};
`;
/* B2B Label Banner */
const B2BLabelBanner = styled.View `
  background-color: ${color.gray15};
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding-vertical: 8px;
  padding-horizontal: 10px;
`;
const B2BBannerText = styled.Text `
  font-family: ${font.pretendardRegular};
  font-size: 14px;
  line-height: 22.4px;
  letter-spacing: -0.07px;
  color: #6a6a73;
`;
